import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DataProviderInstance } from '../../api/DataProvider';
import CreatePatientPopup from '../../popup/CreatePatientPopup/CreatePatientPopup';
import PageHeader from './components/PageHeader/PageHeader';
import SummaryCards from './components/SummaryCards/SummaryCards';
import PatientTable from './components/PatientTable/PatientTable';
import SidePanel from './components/SidePanel/SidePanel';
import RegistrationLinkPopup from './components/RegistrationLinkPopup/RegistrationLinkPopup';
import usePlanLock from '../../hooks/usePlanLock';
import { SUBSCRIPTION_PERMISSIONS } from '../../consts/SubscriptionFeatures';

/**
 * Patients - Main patients page
 *
 * Responsibilities:
 * - Consumes DataProviderInstance() to get patients, isUpdating, GetPatients
 * - Calls GetPatients(true) on mount via useEffect
 * - Manages create/edit popup state
 * - Renders PageHeader + SummaryCards + 2-column layout (PatientTable + SidePanel)
 * - Renders CreatePatientPopup when needed
 */
function Patients() {
  const { patients, patientsLoadedAt, GetPatients, isUpdating } = DataProviderInstance();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 760px)');

  // LGPD: paciente excluído (lápide) fica no DataProvider só para resolver nomes
  // na agenda e no financeiro; a lista, a busca e os contadores o escondem.
  const visiblePatients = useMemo(() => (patients || []).filter((p) => !p.deleted_at), [patients]);

  // Popup state
  const [createPopupVisible, setCreatePopupVisible] = useState(false);
  const [selectedPatientData, setSelectedPatientData] = useState(null);
  const [registrationLinkVisible, setRegistrationLinkVisible] = useState(false);

  const registrationLinkLock = usePlanLock(
    SUBSCRIPTION_PERMISSIONS.PATIENT_REGISTRATION_LINK,
    'O link de cadastro do paciente'
  );

  // Load patients on mount. A lista que o DataProvider acabou de baixar (login, F5)
  // ainda vale: baixar de novo custava mais uma carga inteira numa conta grande (PERF-01).
  useEffect(() => {
    if (patientsLoadedAt && Date.now() - patientsLoadedAt < 60000) return;
    GetPatients(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers
  const handleNewPatient = () => {
    setSelectedPatientData(null);
    setCreatePopupVisible(true);
  };

  const handleEditPatient = (id) => {
    const patient = patients.find((p) => String(p.id) === String(id));
    setSelectedPatientData(patient || null);
    setCreatePopupVisible(true);
  };

  const handleViewPatient = (id) => {
    navigate(`/patients/${id}`);
  };

  const handleSchedulePatient = (id) => {
    // Navigate to appointments with patient pre-selected
    navigate(`/schedule/appointments?patientId=${id}`);
  };

  const handlePopupClose = () => {
    setCreatePopupVisible(false);
    setSelectedPatientData(null);
  };

  return (
    <Box
      sx={{
        backgroundColor: '#F8F9FC',
        minHeight: '100vh',
        p: { xs: 2, md: 3 },
      }}
    >
      {/* Page Header: breadcrumb + title + "New patient" button */}
      <PageHeader
        onNewPatient={handleNewPatient}
        onRegistrationLink={registrationLinkLock.guard(() => setRegistrationLinkVisible(true))}
        registrationLinkLocked={registrationLinkLock.locked}
      />

      {/* Summary Cards: 4 KPI cards */}
      <SummaryCards patients={visiblePatients} />

      {/* Main content: 2-column layout */}
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          mt: 3,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'flex-start',
        }}
      >
        {/* Left: Patient Table */}
        <Box sx={{ flex: 1, minWidth: 0 }} data-tour="patients-list">
          <PatientTable
            patients={visiblePatients}
            isLoading={isUpdating}
            onViewPatient={handleViewPatient}
            onSchedulePatient={handleSchedulePatient}
            onEditPatient={handleEditPatient}
          />
        </Box>

        {/* Right: Side Panel */}
        <Box
          sx={{
            width: isMobile ? '100%' : '280px',
            flexShrink: 0,
          }}
        >
          <SidePanel patients={visiblePatients} />
        </Box>
      </Box>

      {/* Create / Edit Patient Popup */}
      <CreatePatientPopup
        Data={selectedPatientData}
        Visibility={createPopupVisible}
        OnHide={handlePopupClose}
      />

      {registrationLinkVisible && (
        <RegistrationLinkPopup onClose={() => setRegistrationLinkVisible(false)} />
      )}
      {registrationLinkLock.modal}
    </Box>
  );
}

export default Patients;
